<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/no-unused-properties */
import { ref, computed, onMounted, watch } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const { currentAccount } = useAccount();
const currentUser = useMapGetter('getCurrentUser');

// Flag para indicar se o componente já foi montado
const isMounted = ref(false);

// Usar conversationId diretamente das props para evitar problemas de sincronização
const conversationId = computed(() => String(props.conversationId));

// Buscar o chat específico baseado no conversationId das props
const getChat = computed(() => {
  const chats = store.getters.getAllConversations;
  return chats.find(chat => String(chat.id) === conversationId.value) || {};
});

const stormToken = computed(() => {
  return currentUser.value?.ui_settings?.storm_token || '';
});

const contactId = computed(() => getChat.value?.meta?.sender?.id);
const contactGetter = useMapGetter('contacts/getContact');
const contact = computed(() => contactGetter.value(contactId.value));

const cpf = computed(() => {
  return contact.value?.custom_attributes?.cpf || '';
});

const idFichaCliente = computed(() => {
  return getChat.value?.custom_attributes?.id_ficha_cliente || '0';
});

// Obter e formatar o número de telefone
const phoneNumber = computed(() => {
  const phone = contact.value?.phone_number || '';
  if (!phone) return '';

  // Remove todos os caracteres não numéricos
  let cleanPhone = phone.replace(/[^\d]/g, '');

  // Remove o código do país +55 se existir
  if (cleanPhone.startsWith('55')) {
    cleanPhone = cleanPhone.substring(2);
  }

  return cleanPhone;
});

// Obter o domínio da conta
const accountDomain = computed(() => {
  const domain = currentAccount.value?.domain;
  if (!domain) return '';

  // Verificar se o domínio começa com http:// ou https://
  if (!domain.startsWith('http://') && !domain.startsWith('https://')) {
    // Se não começar com protocolo válido, retornar vazio
    return '';
  }

  // Garantir que o domínio termine com /
  return domain.endsWith('/') ? domain : `${domain}/`;
});

// Função para validar CPF
const isValidCPF = cpfValue => {
  // Remove caracteres não numéricos
  const cleanCPF = cpfValue.replace(/[^\d]/g, '');

  // Verifica se tem 11 dígitos
  if (cleanCPF.length !== 11) return false;

  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1{10}$/.test(cleanCPF)) return false;

  // Validação dos dígitos verificadores
  let sum = 0;
  let remainder;

  // Validação do primeiro dígito
  for (let i = 1; i <= 9; i += 1) {
    sum += parseInt(cleanCPF.substring(i - 1, i), 10) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCPF.substring(9, 10), 10)) return false;

  // Validação do segundo dígito
  sum = 0;
  for (let i = 1; i <= 10; i += 1) {
    sum += parseInt(cleanCPF.substring(i - 1, i), 10) * (12 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCPF.substring(10, 11), 10)) return false;

  return true;
};

const hasCPF = computed(() => {
  return cpf.value && cpf.value.trim() !== '';
});

const isCPFValid = computed(() => {
  return hasCPF.value && isValidCPF(cpf.value);
});

const cpfErrorMessage = computed(() => {
  if (!hasCPF.value) {
    return 'CPF não informado. É necessário que o contato tenha um CPF cadastrado para acessar o sistema de simulação.';
  }
  if (!isCPFValid.value) {
    return 'CPF inválido. Verifique se o CPF cadastrado está correto para acessar o sistema de simulação.';
  }
  return '';
});

const canShowIframe = computed(() => {
  return stormToken.value && isCPFValid.value && accountDomain.value;
});

const iframeUrl = computed(() => {
  if (!canShowIframe.value) {
    return '';
  }

  const id = conversationId.value;
  const baseUrl = `${accountDomain.value}consignado/Seguranca/autenticarTokenRedirecionar?token=${stormToken.value}&redirect=/consignado/Vendas/montaFichaClienteChatStorm/`;
  const fullUrl = `${baseUrl}${idFichaCliente.value}/${cpf.value}/${id}/${phoneNumber.value}`;

  return fullUrl;
});

const closeCustomInfoPanel = () => {
  emit('close');
};

const refreshIframe = () => {
  // Forçar recarga do iframe
  const iframe = document.querySelector(
    `#storm-iframe-${conversationId.value}`
  );
  if (iframe) {
    const currentSrc = iframe.src;
    iframe.src = '';
    iframe.src = currentSrc;
  }
};

// Criar array de botões para o header
const headerButtons = computed(() => {
  if (!canShowIframe.value) return [];

  return [
    {
      key: 'refresh',
      icon: 'i-lucide-refresh-cw',
      tooltip: 'Recarregar',
    },
  ];
});

const handleHeaderButtonClick = key => {
  if (key === 'refresh') {
    refreshIframe();
  }
};

// Garantir que os dados do contato sejam carregados apenas uma vez
onMounted(() => {
  if (!isMounted.value && contactId.value) {
    store.dispatch('contacts/show', { id: contactId.value });
    isMounted.value = true;
  }
});

// Observar mudanças no contactId apenas se ainda não foi montado
watch(contactId, newContactId => {
  if (!isMounted.value && newContactId) {
    store.dispatch('contacts/show', { id: newContactId });
  }
});
</script>

<template>
  <div class="w-full h-full flex flex-col">
    <SidebarActionsHeader
      :title="`Simular e Digitar${cpf ? ` - ${cpf}` : ''}`"
      :buttons="headerButtons"
      @close="closeCustomInfoPanel"
      @click="handleHeaderButtonClick"
    />
    <div v-if="!stormToken" class="p-6 text-center">
      <p class="text-sm text-n-slate-11">
        Sua sessão expirou no Storm. Realize um novo login no Chat Storm para
        continuar acessando as informações do sistema.
      </p>
    </div>
    <div v-else-if="!accountDomain" class="p-6 text-center">
      <p class="text-sm text-n-slate-11">
        Domínio do Storm não configurado. Entre em contato com o administrador
        do sistema.
      </p>
    </div>
    <div v-else-if="!isCPFValid" class="p-6 text-center">
      <div class="flex flex-col items-center gap-4">
        <div
          class="w-16 h-16 rounded-full bg-n-red-3 flex items-center justify-center"
        >
          <i class="i-lucide-alert-circle text-2xl text-n-red-11" />
        </div>
        <div>
          <h3 class="text-base font-medium text-n-slate-12 mb-2">
            Não é possível acessar o sistema
          </h3>
          <p class="text-sm text-n-slate-11">
            {{ cpfErrorMessage }}
          </p>
        </div>
      </div>
    </div>
    <div v-else class="flex-1 relative">
      <iframe
        :id="`storm-iframe-${conversationId}`"
        :src="iframeUrl"
        class="w-full h-full border-0"
        :title="`Simular e Digitar - ${cpf}`"
        sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
      />
    </div>
  </div>
</template>
